/**
 * Pure helpers for the blocks list of the page editor: per-instance summaries shown on card
 * headers, deep duplication, insertion-index resolution for targeted adds, and the drag-and-drop
 * MIME/geometry helpers shared by the components panel. No side-effectful imports : this file is
 * unit-tested in isolation.
 */

/** Custom MIME type for card drags: drags that don't originate from a card handle (text
 *  selections, files, links) carry no payload under this key and are ignored on drop. */
export const COMPONENT_DRAG_MIME = "application/x-page-component-dnd";

export const SUMMARY_MAX_LENGTH = 60;

type EditorComponentLike = {
	componentType?: unknown;
	content?: unknown;
	key?: unknown;
};

/** Fields probed in order for the per-instance summary; mirrors the most common text-bearing
 *  fields across the component library. */
const SUMMARY_PRIORITY_FIELDS = ["title", "heading", "text", "label", "description", "eyebrow"] as const;

const asMeaningfulString = (value: unknown): string | null => {
	if (typeof value !== "string") return null;
	const collapsed = value.replace(/\s+/g, " ").trim();
	return collapsed ? collapsed : null;
};

const truncateSummary = (value: string): string =>
	value.length <= SUMMARY_MAX_LENGTH ? value : `${value.slice(0, SUMMARY_MAX_LENGTH).trimEnd()}…`;

/**
 * One-line excerpt of THIS instance's content for the card header: first non-empty string among
 * title/heading/text/label/description/eyebrow, else the first non-empty top-level string field,
 * else null. Truncated at ~60 chars with an ellipsis.
 */
export const componentInstanceSummary = (component: EditorComponentLike | null | undefined): string | null => {
	const content = component?.content;
	if (!content || typeof content !== "object" || Array.isArray(content)) return null;
	const record = content as Record<string, unknown>;
	for (const field of SUMMARY_PRIORITY_FIELDS) {
		const candidate = asMeaningfulString(record[field]);
		if (candidate) return truncateSummary(candidate);
	}
	for (const key of Object.keys(record)) {
		if ((SUMMARY_PRIORITY_FIELDS as readonly string[]).includes(key)) continue;
		const candidate = asMeaningfulString(record[key]);
		if (candidate) return truncateSummary(candidate);
	}
	return null;
};

/**
 * Returns a new list with an INDEPENDENT deep copy of `components[index]` inserted right below the
 * original. The runtime `key` is dropped from the clone (keys are index-derived and regenerated by
 * normalizeComponents); the other entries keep their references. Null when the index is invalid.
 */
export const duplicateComponentAt = <T extends EditorComponentLike>(
	components: readonly T[],
	index: number
): T[] | null => {
	const source = components[index];
	if (!source) return null;
	const { key: _key, ...rest } = source as Record<string, unknown>;
	const clone = (
		typeof structuredClone === "function"
			? // @ts-ignore - structuredClone might not be in the TS lib target
			  structuredClone(rest)
			: // JSON fallback is fine: component content is plain JSON data.
			  JSON.parse(JSON.stringify(rest))
	) as T;
	const next = [...components];
	next.splice(index + 1, 0, clone);
	return next;
};

/**
 * Where a new component should land: an explicit gap index wins (clamped to the list), otherwise
 * right after the active card when one is open, otherwise the end of the list.
 */
export const resolveInsertionIndex = (
	explicitIndex: number | null | undefined,
	activeIndex: number | null | undefined,
	length: number
): number => {
	if (typeof explicitIndex === "number" && Number.isInteger(explicitIndex) && explicitIndex >= 0) {
		return Math.min(explicitIndex, length);
	}
	if (typeof activeIndex === "number" && Number.isInteger(activeIndex) && activeIndex >= 0 && activeIndex < length) {
		return activeIndex + 1;
	}
	return length;
};

/** Insertion index for a pointer hovering a card: before the card when above its vertical
 *  midpoint, after it otherwise. */
export const insertionIndexFromPointer = (
	clientY: number,
	rect: { top: number; height: number },
	cardIndex: number
): number => (clientY < rect.top + rect.height / 2 ? cardIndex : cardIndex + 1);
